'use client';

import React, { useState, useRef, useEffect } from 'react';
import Link from 'next/link';
import dynamic from 'next/dynamic';
import { useSidebar } from '@/context/SidebarContext';
import { FiX } from 'react-icons/fi';
import { navItems } from './bottom-nav-items';

const loadContactDropdownContent = () => import('./ContactDropdownContent');

const ContactDropdownDesktop = dynamic(
  () => loadContactDropdownContent().then(mod => mod.ContactDropdownDesktop),
  {
    loading: () => (
      <>
        <div className="h-[300px] animate-pulse rounded bg-gray-100" />
        <div className="h-[300px] animate-pulse rounded bg-gray-100" />
        <div className="h-[300px] animate-pulse rounded bg-gray-100" />
      </>
    ),
  }
);

const ContactDropdownMobile = dynamic(
  () => loadContactDropdownContent().then(mod => mod.ContactDropdownMobile),
  {
    loading: () => (
      <div className="h-[420px] animate-pulse rounded-lg bg-white/10" />
    ),
  }
);

const preloadContactDropdown = () => {
  void loadContactDropdownContent();
};

const loadSidebarNavigation = () => import('@/components/collection/SidebarNavigation');

const SidebarNavigation = dynamic(loadSidebarNavigation, {
  loading: () => null,
});

const preloadSidebarNavigation = () => {
  void loadSidebarNavigation();
};

const loadDropdownPanelContent = () => import('./DropdownPanelContent');

const VarietalDropdownPanel = dynamic(
  () => loadDropdownPanelContent().then(mod => mod.VarietalDropdownPanel),
  { loading: () => <DropdownPanelSkeleton columns={4} /> }
);

const RegionDropdownPanel = dynamic(
  () => loadDropdownPanelContent().then(mod => mod.RegionDropdownPanel),
  { loading: () => <DropdownPanelSkeleton /> }
);

const SakeSojuDropdownPanel = dynamic(
  () => loadDropdownPanelContent().then(mod => mod.SakeSojuDropdownPanel),
  { loading: () => <DropdownPanelSkeleton columns={4} /> }
);

const SpiritsDropdownPanel = dynamic(
  () => loadDropdownPanelContent().then(mod => mod.SpiritsDropdownPanel),
  { loading: () => <DropdownPanelSkeleton columns={4} /> }
);

const DealsDropdownPanel = dynamic(
  () => loadDropdownPanelContent().then(mod => mod.DealsDropdownPanel),
  { loading: () => <DropdownPanelSkeleton columns={4} /> }
);

const MobileMenuContent = dynamic(
  () => loadDropdownPanelContent().then(mod => mod.MobileMenuContent),
  { loading: () => <div className="h-[360px] animate-pulse rounded-lg bg-white/10" /> }
);

const preloadDropdownPanelContent = () => {
  void loadDropdownPanelContent();
};

// 컴포넌트 정의
// --------------------------------------
// 드롭다운 메뉴 컴포넌트 (공통)
interface DropdownProps {
  isOpen: boolean;
  onClose: () => void;
  position: { top: number; left: number };
  children: React.ReactNode;
  columns?: number;
}

const DropdownMenu = ({ isOpen, position, children, columns = 3 }: DropdownProps) => {
  if (!isOpen) return null;

  const gridClass = columns === 4 ? "grid-cols-4" : "grid-cols-3";

  return (
    <div
      className={`fixed bg-white shadow-xl z-[100] pt-6 pb-3 px-3 grid ${gridClass} gap-2 border border-gray-300`}
      style={{
        top: `${position.top}px`,
        left: `${position.left}px`,
        width: '820px',
        transform: 'translateX(22px)'
      }}
    >
      {children}
    </div>
  );
};

// 메뉴 링크 컴포넌트
function DropdownPanelSkeleton({ columns = 3 }: { columns?: number }) {
  return (
    <>
      {Array.from({ length: columns }).map((_, index) => (
        <div
          key={index}
          className="h-[260px] animate-pulse rounded bg-gray-100"
        />
      ))}
    </>
  );
}

  // 모바일 메뉴 타입별 타이틀 반환 함수
  const getMobileMenuTitle = (type: string | null) => {
    switch (type) {
      case 'varietal':
        return 'Wine Varietal';
      case 'region':
        return 'Wine Region';
      case 'spirits':
        return 'Spirits';
      case 'sakesoju':
        return 'Sake & Soju';
      case 'deals':
        return 'Deals';
      case 'contact':
        return 'Contact & Map';
      default:
        return '';
    }
  };

// 메인 컴포넌트
// --------------------------------------
const BottomNav = () => {
  const { isSidebarOpen, openSidebar, closeSidebar } = useSidebar();
  const [isVarietalOpen, setIsVarietalOpen] = useState(false);
  const [isRegionOpen, setIsRegionOpen] = useState(false);
  const [isSpiritsOpen, setIsSpiritsOpen] = useState(false);
  const [isSakeSojuOpen, setIsSakeSojuOpen] = useState(false);
  const [isDealsOpen, setIsDealsOpen] = useState(false);
  const [isContactOpen, setIsContactOpen] = useState(false);
  const [isMobileModalOpen, setIsMobileModalOpen] = useState(false);
  const [mobileMenuType, setMobileMenuType] = useState<string | null>(null);
  const [isMobile, setIsMobile] = useState(false);

  const navContainerRef = useRef<HTMLDivElement>(null);
  const varietalRef = useRef<HTMLDivElement>(null);
  const regionRef = useRef<HTMLDivElement>(null);
  const spiritsRef = useRef<HTMLDivElement>(null);
  const sakeSojuRef = useRef<HTMLDivElement>(null);
  const dealsRef = useRef<HTMLDivElement>(null);
  const contactRef = useRef<HTMLDivElement>(null);
  
  // 모바일 환경 체크
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // 드롭다운 위치 계산
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0 });

  // Function to update dropdown position - called on mount, resize, and when dropdown opens
  const updateDropdownPosition = React.useCallback(() => {
    if (navContainerRef.current) {
      const rect = navContainerRef.current.getBoundingClientRect();
      const containerLeft = rect.left;
      const containerTop = rect.bottom;
      setDropdownPosition({
        top: containerTop,
        left: containerLeft + 1
      });
    }
  }, []);

  useEffect(() => {
    updateDropdownPosition();
    window.addEventListener('resize', updateDropdownPosition);
    window.addEventListener('scroll', updateDropdownPosition);
    return () => {
      window.removeEventListener('resize', updateDropdownPosition);
      window.removeEventListener('scroll', updateDropdownPosition);
    };
  }, [updateDropdownPosition]);

  // 드롭다운 핸들러 함수
  const handleAllClick = (e: React.MouseEvent) => {
    e.preventDefault();
    preloadSidebarNavigation();
    openSidebar();
  };

  const handleVarietalClick = (e: React.MouseEvent) => {
    e.preventDefault();
    updateDropdownPosition();
    setIsVarietalOpen(!isVarietalOpen);
    setIsRegionOpen(false);
    setIsSpiritsOpen(false);
    setIsSakeSojuOpen(false);
    setIsDealsOpen(false);
    setIsContactOpen(false);
  };

  const handleRegionClick = (e: React.MouseEvent) => {
    e.preventDefault();
    updateDropdownPosition();
    setIsRegionOpen(!isRegionOpen);
    setIsVarietalOpen(false);
    setIsSpiritsOpen(false);
    setIsSakeSojuOpen(false);
    setIsDealsOpen(false);
    setIsContactOpen(false);
  };

  const handleSpiritsClick = (e: React.MouseEvent) => {
    e.preventDefault();
    updateDropdownPosition();
    setIsSpiritsOpen(!isSpiritsOpen);
    setIsVarietalOpen(false);
    setIsRegionOpen(false);
    setIsSakeSojuOpen(false);
    setIsDealsOpen(false);
    setIsContactOpen(false);
  };

  const handleSakeSojuClick = (e: React.MouseEvent) => {
    e.preventDefault();
    updateDropdownPosition();
    setIsSakeSojuOpen(!isSakeSojuOpen);
    setIsVarietalOpen(false);
    setIsRegionOpen(false);
    setIsSpiritsOpen(false);
    setIsDealsOpen(false);
    setIsContactOpen(false);
  };

  const handleDealsClick = (e: React.MouseEvent) => {
    e.preventDefault();
    updateDropdownPosition();
    setIsDealsOpen(!isDealsOpen);
    setIsVarietalOpen(false);
    setIsRegionOpen(false);
    setIsSpiritsOpen(false);
    setIsSakeSojuOpen(false);
    setIsContactOpen(false);
  };

  const handleContactClick = (e: React.MouseEvent) => {
    e.preventDefault();
    updateDropdownPosition();
    setIsContactOpen(!isContactOpen);
    setIsVarietalOpen(false);
    setIsRegionOpen(false);
    setIsSpiritsOpen(false);
    setIsSakeSojuOpen(false);
    setIsDealsOpen(false);
  };

  // 드롭다운 닫기 함수
  const closeVarietalDropdown = () => setIsVarietalOpen(false);
  const closeRegionDropdown = () => setIsRegionOpen(false);
  const closeSpiritsDropdown = () => setIsSpiritsOpen(false);
  const closeSakeSojuDropdown = () => setIsSakeSojuOpen(false);
  const closeDealsDropdown = () => setIsDealsOpen(false);
  const closeContactDropdown = () => setIsContactOpen(false);

  // 바깥을 클릭했을 때 드롭다운 닫기
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        (varietalRef.current && !varietalRef.current.contains(event.target as Node)) &&
        (regionRef.current && !regionRef.current.contains(event.target as Node)) &&
        (spiritsRef.current && !spiritsRef.current.contains(event.target as Node)) &&
        (sakeSojuRef.current && !sakeSojuRef.current.contains(event.target as Node)) &&
        (dealsRef.current && !dealsRef.current.contains(event.target as Node)) &&
        (contactRef.current && !contactRef.current.contains(event.target as Node))
      ) {
        setIsVarietalOpen(false);
        setIsRegionOpen(false);
        setIsSpiritsOpen(false);
        setIsSakeSojuOpen(false);
        setIsDealsOpen(false);
        setIsContactOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // 와인 품종 드롭다운 렌더링
  const renderVarietalDropdown = () => (
    <DropdownMenu isOpen={isVarietalOpen} onClose={closeVarietalDropdown} position={dropdownPosition} columns={4}>
      <VarietalDropdownPanel onClose={closeVarietalDropdown} />
    </DropdownMenu>
  );

  const renderRegionDropdown = () => (
    <DropdownMenu isOpen={isRegionOpen} onClose={closeRegionDropdown} position={dropdownPosition}>
      <RegionDropdownPanel onClose={closeRegionDropdown} />
    </DropdownMenu>
  );

  const renderSakeSojuDropdown = () => (
    <DropdownMenu isOpen={isSakeSojuOpen} onClose={closeSakeSojuDropdown} position={dropdownPosition} columns={4}>
      <SakeSojuDropdownPanel onClose={closeSakeSojuDropdown} />
    </DropdownMenu>
  );

  const renderSpiritsDropdown = () => (
    <DropdownMenu isOpen={isSpiritsOpen} onClose={closeSpiritsDropdown} position={dropdownPosition} columns={4}>
      <SpiritsDropdownPanel onClose={closeSpiritsDropdown} />
    </DropdownMenu>
  );

  const renderDealsDropdown = () => (
    <DropdownMenu isOpen={isDealsOpen} onClose={closeDealsDropdown} position={dropdownPosition} columns={4}>
      <DealsDropdownPanel onClose={closeDealsDropdown} />
    </DropdownMenu>
  );
  // Contact & Map 드롭다운 렌더링
  // Contact & Map dropdown rendering
  const renderContactDropdown = () => (
    <DropdownMenu isOpen={isContactOpen} onClose={closeContactDropdown} position={dropdownPosition}>
      <ContactDropdownDesktop />
    </DropdownMenu>
  );

  // Mobile menu handler
  const handleMobileMenuClick = (menuType: string) => {
    if (menuType === 'contact') {
      preloadContactDropdown();
    } else {
      preloadDropdownPanelContent();
    }

    setMobileMenuType(menuType);
    setIsMobileModalOpen(true);
  };

  const handleCloseMobileModal = () => {
    setIsMobileModalOpen(false);
    setMobileMenuType(null);
  };

  // 모바일 메뉴 컨텐츠 렌더링
  const renderMobileMenuContent = () => {
    if (mobileMenuType === 'contact') {
      return <ContactDropdownMobile />;
    }

    return (
      <MobileMenuContent
        menuType={mobileMenuType}
        onClose={handleCloseMobileModal}
      />
    );
  };

  return (
    <div className="relative">
      <nav className="w-full bg-[#374151] text-white">
        <div className="w-full max-w-[1280px] mx-auto px-2 sm:px-4 lg:px-6" ref={navContainerRef}>
          <div className="flex items-center space-x-4 sm:space-x-6 overflow-x-auto py-1 scrollbar-hide">
            {navItems.map((item) => (
              item.label === 'All' ? (
                <button
                  key={item.label}
                  onClick={handleAllClick}
                  onMouseEnter={preloadSidebarNavigation}
                  onFocus={preloadSidebarNavigation}
                  className="whitespace-nowrap hover:text-yellow-200 transition-colors duration-200 flex-shrink-0 font-medium text-sm cursor-pointer bg-transparent border-0 text-white p-0"
                >
                  {item.label}
                </button>
              ) : item.label === 'Wine Varietal' ? (
                <div key={item.label} className="relative" ref={varietalRef}>
                  <button
                    onClick={isMobile ? () => handleMobileMenuClick('varietal') : handleVarietalClick}
                    onMouseEnter={preloadDropdownPanelContent}
                    onFocus={preloadDropdownPanelContent}
                    className="whitespace-nowrap hover:text-yellow-200 transition-colors duration-200 flex-shrink-0 font-medium text-sm cursor-pointer bg-transparent border-0 text-white p-0 flex items-center"
                  >
                    {item.label}
                  </button>
                  {!isMobile && isVarietalOpen && renderVarietalDropdown()}
                </div>
              ) : item.label === 'Wine Region' ? (
                <div key={item.label} className="relative" ref={regionRef}>
                  <button
                    onClick={isMobile ? () => handleMobileMenuClick('region') : handleRegionClick}
                    onMouseEnter={preloadDropdownPanelContent}
                    onFocus={preloadDropdownPanelContent}
                    className="whitespace-nowrap hover:text-yellow-200 transition-colors duration-200 flex-shrink-0 font-medium text-sm cursor-pointer bg-transparent border-0 text-white p-0 flex items-center"
                  >
                    {item.label}
                  </button>
                  {!isMobile && isRegionOpen && renderRegionDropdown()}
                </div>
              ) : item.label === 'Spirits' ? (
                <div key={item.label} className="relative" ref={spiritsRef}>
                  <button
                    onClick={isMobile ? () => handleMobileMenuClick('spirits') : handleSpiritsClick}
                    onMouseEnter={preloadDropdownPanelContent}
                    onFocus={preloadDropdownPanelContent}
                    className="whitespace-nowrap hover:text-yellow-200 transition-colors duration-200 flex-shrink-0 font-medium text-sm cursor-pointer bg-transparent border-0 text-white p-0 flex items-center"
                  >
                    {item.label}
                    {item.isHot && (
                      <span
                        className="ml-1.5 px-0.5 py-0 bg-red-600 text-white text-[10px] font-bold rounded inline-block"
                        style={{ transform: 'rotate(-10deg)' }}
                      >
                        HOT!
                      </span>
                    )}
                  </button>
                  {!isMobile && isSpiritsOpen && renderSpiritsDropdown()}
                </div>
              ) : item.label === 'Sake & Soju' ? (
                <div key={item.label} className="relative" ref={sakeSojuRef}>
                  <button
                    onClick={isMobile ? () => handleMobileMenuClick('sakesoju') : handleSakeSojuClick}
                    onMouseEnter={preloadDropdownPanelContent}
                    onFocus={preloadDropdownPanelContent}
                    className="whitespace-nowrap hover:text-yellow-200 transition-colors duration-200 flex-shrink-0 font-medium text-sm cursor-pointer bg-transparent border-0 text-white p-0 flex items-center"
                  >
                    {item.label}
                  </button>
                  {!isMobile && isSakeSojuOpen && renderSakeSojuDropdown()}
                </div>
              ) : item.label === 'Deals' ? (
                <div key={item.label} className="relative" ref={dealsRef}>
                  <button
                    onClick={isMobile ? () => handleMobileMenuClick('deals') : handleDealsClick}
                    onMouseEnter={preloadDropdownPanelContent}
                    onFocus={preloadDropdownPanelContent}
                    className="whitespace-nowrap hover:text-yellow-200 transition-colors duration-200 flex-shrink-0 font-medium text-sm cursor-pointer bg-transparent border-0 text-white p-0 flex items-center"
                  >
                    {item.label}
                    {item.isHot && (
                      <span
                        className="ml-1.5 px-0.5 py-0 bg-red-600 text-white text-[10px] font-bold rounded inline-block"
                        style={{ transform: 'rotate(-10deg)' }}
                      >
                        HOT!
                      </span>
                    )}
                  </button>
                  {!isMobile && isDealsOpen && renderDealsDropdown()}
                </div>
              ) : item.label === 'Contact & Map' ? (
                <div key={item.label} className="relative" ref={contactRef}>
                  <button
                    onClick={isMobile ? () => handleMobileMenuClick('contact') : handleContactClick}
                    onMouseEnter={preloadContactDropdown}
                    onFocus={preloadContactDropdown}
                    className="whitespace-nowrap hover:text-yellow-200 transition-colors duration-200 flex-shrink-0 font-medium text-sm cursor-pointer bg-transparent border-0 text-white p-0 flex items-center"
                  >
                    {item.label}
                  </button>
                  {!isMobile && isContactOpen && renderContactDropdown()}
                </div>
              ) : (
                <Link
                  key={item.label}
                  href={item.href}
                  className="whitespace-nowrap hover:text-yellow-200 transition-colors duration-200 flex-shrink-0 font-medium text-sm"
                >
                  {item.label}
                </Link>
              )
            ))}
          </div>
        </div>
      </nav>
      
      <SidebarNavigation isOpen={isSidebarOpen} onClose={closeSidebar} />

      {/* 모바일 메뉴 모달 */}
      {isMobileModalOpen && (
        <div className="fixed inset-0 z-50 p-2">
          <div 
            className="absolute inset-0 bg-black/60 backdrop-blur-xs transition-opacity"
            onClick={handleCloseMobileModal}
          />
          <div className="absolute inset-y-2 left-2 w-3/4 bg-gray-500 bg-opacity-95 shadow-2xl rounded-lg flex flex-col">
            {/* 헤더 */}
            <div className="flex-none flex items-center justify-between border-b border-gray-400 bg-transparent rounded-t-lg py-4 pr-4">
              <div className="flex items-baseline space-x-4">
                <h2 className="text-4xl font-extrabold text-white pl-2">
                  {getMobileMenuTitle(mobileMenuType)}
                </h2>
              </div>
              <button
                onClick={handleCloseMobileModal}
                className="w-8 h-8 flex items-center justify-center rounded-full border border-gray-300 text-white hover:text-gray-100 hover:bg-gray-600 transition-colors"
              >
                <FiX size={18} />
              </button>
            </div>

            {/* 컨텐츠 */}
            <div className="flex-1 p-3 overflow-y-auto">
              <div className="text-white">
                {renderMobileMenuContent()}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default BottomNav;
